import type { ApiKeyEntity } from "../../services/apiKeysService";
import type { AssignedRole, EffectiveSummary } from "../../types/access.types";

/**
 * Everything the API keys list needs to paint one page, shared by the table and the cards so the two faces of
 * the same data cannot drift apart — the same arrangement `accessUsersList.types.ts` uses.
 */
export interface AccessApiKeysListProps {
  /** The keys left after the local filters. */
  keys: ApiKeyEntity[];
  /** Assigned roles per key id. A missing entry is "none", not "unknown" — see `rolesUnavailable`. */
  rolesByKey: Record<string, AssignedRole[]>;
  /** Key ids whose roles fetch FAILED. Their access is unknown, never confirmed as none. */
  rolesUnavailable: ReadonlySet<string>;
  /** Effective access per key id, resolved deny-first against the roles catalog. */
  summaryById: Record<string, EffectiveSummary>;
  /** The roles catalog 403'd: effective access cannot be resolved and role management is off. */
  catalogUnavailable: boolean;
  /** Whether the viewer may change role assignments — decides the action's icon and wording. */
  canManageRoles: boolean;
  /** Whether the viewer may edit IPs and enable/disable — decides whether the lifecycle menu renders. */
  canUpdate: boolean;
  /** Relative "last used" label, already translated by the section. */
  lastUsedLabel: (key: ApiKeyEntity) => string | null;
  onOpenRoles: (key: ApiKeyEntity) => void;
  onEditIps: (key: ApiKeyEntity) => void;
  onDisable: (key: ApiKeyEntity) => void;
  onEnable: (key: ApiKeyEntity) => void;
}

/**
 * Whether the assign-roles drawer can be offered for this key.
 *
 * The drawer resolves its role list against the catalog, so without it it would offer "no roles available";
 * and opening it for a key whose roles are UNKNOWN would render every box clear, indistinguishable from a
 * confirmed "holds nothing". Shared so the table and the cards cannot answer differently.
 */
export function canOpenKeyRolesFor(
  keyId: string,
  catalogUnavailable: boolean,
  rolesUnavailable: ReadonlySet<string>,
): boolean {
  return !catalogUnavailable && !rolesUnavailable.has(keyId);
}
